import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {JSFeatureFlag} from '@github-ui/feature-flags/client-feature-flags'
import {useCachePreheatQueries, useCachePreheatQuery} from '@github-ui/relay-query-cache/hooks/use-cache-preheat-query'
import {useMemo} from 'react'

import {IssueViewerViewGraphqlQuery} from '../components/IssueViewer'
import {IssueViewerSecondaryGraphqlQuery} from '../components/IssueViewerSecondaryView'

type UseCachePreheatIssueViewerProps = {
  owner: string
  repo: string
  number: number
  source: string
  featureFlag: JSFeatureFlag
  enabledForRepo?: boolean
}

type UseCachePreheatIssueViewerQueriesProps = {
  issues: Array<{
    owner: string
    repo: string
    number: number
  }>
  source: string
  featureFlag: JSFeatureFlag
  enabledForRepo?: boolean
}

export function useCachePreheatIssueViewer({
  owner,
  repo,
  number,
  source,
  featureFlag,
  enabledForRepo,
}: UseCachePreheatIssueViewerProps) {
  const preHeatingEnabled = isFeatureEnabled(featureFlag) || !!enabledForRepo
  const preHeatingEnabledForSecondary = isFeatureEnabled('issues_preheating_secondary')
  const excludeFromSecondaryQueryCacheKey = useMemo(() => ['markAsRead'], [])

  useCachePreheatQuery({
    query: IssueViewerViewGraphqlQuery,
    variables: {
      owner,
      repo,
      number,
      count: 15,
    },
    source,
    enabled: preHeatingEnabled,
    highPriority: true,
  })

  useCachePreheatQuery({
    query: IssueViewerSecondaryGraphqlQuery,
    variables: {
      owner,
      repo,
      number,
      markAsRead: false,
    },
    source,
    enabled: preHeatingEnabled && preHeatingEnabledForSecondary,
    excludeFromCacheKey: excludeFromSecondaryQueryCacheKey,
  })
}

export function useCachePreheatIssueViewerQueries({
  issues,
  source,
  featureFlag,
  enabledForRepo,
}: UseCachePreheatIssueViewerQueriesProps) {
  const preHeatingEnabled = isFeatureEnabled(featureFlag) || !!enabledForRepo
  const preHeatingEnabledForSecondary = isFeatureEnabled('issues_preheating_secondary')
  const excludeFromSecondaryQueryCacheKey = useMemo(() => ['markAsRead'], [])

  const queries = useMemo(() => {
    const primaryQueries = issues.map(({owner, repo, number}) => ({
      query: IssueViewerViewGraphqlQuery,
      variables: {
        owner,
        repo,
        number,
        count: 15,
      },
      source,
      enabled: preHeatingEnabled,
      highPriority: true,
    }))

    const secondaryQueries = issues.map(({owner, repo, number}) => ({
      query: IssueViewerSecondaryGraphqlQuery,
      variables: {
        owner,
        repo,
        number,
        markAsRead: false,
      },
      source,
      enabled: preHeatingEnabled && preHeatingEnabledForSecondary,
      excludeFromCacheKey: excludeFromSecondaryQueryCacheKey,
    }))

    return [...primaryQueries, ...secondaryQueries]
  }, [issues, source, preHeatingEnabled, preHeatingEnabledForSecondary, excludeFromSecondaryQueryCacheKey])

  useCachePreheatQueries({queries})
}
